/**
 * How a picture that came OUT of an uploaded document is named on the wire.
 *
 * The extractor names a picture for the PAGE it sat on and nothing else —
 * `slide-01-image1.jpg`, minted in `pipeline/source-render.ts` — so two
 * uploaded PDFs collide by construction, both carrying a `slide-01-image1.jpg`
 * from their own first page. Until 2026-08-06 the wizard only ever extracted
 * the FIRST document, which hid the problem; the moment it extracts every
 * document, binning one document's page-1 picture would silently bin the
 * other's, and a caption would attach to whichever was written to the bundle
 * first.
 *
 * So a picture is identified by the upload it came out of as well as its name.
 * The upload id is the one identifier the client and the server already agree
 * on without either re-deriving a filename — which they cannot do, because
 * `safeName()` and the bundle's collision suffix both rewrite names server-side
 * after the author has already been shown the originals.
 *
 * BARE NAMES STILL MEAN WHAT THEY MEANT. A key with no upload id in front of it
 * matches every document's copy of that name. That was the only sentence it
 * could mean back when one document was all the wizard read, and narrowing it
 * now would silently start keeping pictures somebody asked to lose. It is also
 * the honest answer for a caller with no upload store behind it (a direct API
 * build, or the wizard's own re-upload retry after a store handle expires),
 * where there is no id to qualify with.
 */

/** `<uploadId>:<name>`, or the bare name when there is no upload behind it —
 *  so a document with no store handle degrades to the old behaviour at every
 *  call site without needing a branch. */
export function mediaKey(uploadId: string | undefined, name: string): string {
  return uploadId ? `${uploadId}:${name}` : name;
}

/**
 * Split a key back into the upload it names and the picture's own name.
 *
 * Qualified ONLY when the first 32 characters are an upload id — lowercase hex,
 * the shape `stashUpload` mints (`pipeline/upload-store.ts`, `ID_RE`) — and the
 * 33rd is the colon. Anything else is a bare filename, including one that
 * happens to contain a colon of its own: an author's upload keeps its raw
 * client filename, and on a Linux browser that can hold almost any byte.
 *
 * The name is taken by offset rather than by splitting on the separator, so a
 * picture called `a:b.jpg` survives the round trip intact.
 */
export function parseMediaKey(key: string): { uploadId: string; name: string } {
  if (key.length > 33 && key[32] === ':' && /^[0-9a-f]{32}$/.test(key.slice(0, 32))) {
    return { uploadId: key.slice(0, 32), name: key.slice(33) };
  }
  return { uploadId: '', name: key };
}
